import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, ConfirmDeleteModal, Theme } from '../../components';
import Table from '../../components/table/Table';
import {
  DepartmentTemplate,
  DepartmentTemplateField,
  FetchCrudData,
  GetTableDataOptions,
  SORT_ORDER,
  Store,
  StoreField,
  TableColumn,
} from '../../models';
import { departmentTemplatesActions, departmentTemplatesSelectors, storeActions, storeSelectors } from '../../redux';
import { ITEMS_PER_PAGE } from '../constants';
import AddEditDepartmentTemplateModal from './components/AddEditDepartmentTemplateModal';
import AddEditStoreModal from './components/AddEditStore';
import ManageDepartmentTemplatesModal from './components/ManageDepartmentTemplates';

import './admin.scss';

const STORE_COLUMNS: TableColumn[] = [
  {
    label: 'lblId',
    field: StoreField.EXTERNAL_ID,
  },
  {
    label: 'lblName',
    field: StoreField.NAME,
  },
  {
    label: 'lblDepartments',
    field: StoreField.DEPARTMENTS,
    sortable: false,
  },
  {
    label: 'lblLanguage',
    field: StoreField.DEFAULT_LANGUAGE,
    sortable: false,
  },
  {
    label: '',
    field: 'actions',
    sortable: false,
  },
];

export type StoreModal =
  | 'ADD_EDIT_STORE'
  | 'DELETE_STORE'
  | 'MANAGE_DEPARTMENT_TEMPLATES'
  | 'ADD_EDIT_DEPARTMENT_TEMPLATE'
  | 'DELETE_DEPARTMENT_TEMPLATE';

const AdminStoreAndDepartment: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const storesAndDepartments = useSelector(storeSelectors.getStores);
  const isLoading = useSelector(storeSelectors.isLoading);
  const total = useSelector(storeSelectors.getTotal);
  const isDeleteStoreLoading = useSelector(storeSelectors.isDeleteLoading);
  const isAddEditStoreLoading = useSelector(storeSelectors.isAddEditLoading);
  const isDeleteDepartmentTemplateLoading = useSelector(departmentTemplatesSelectors.isDeleteLoading);
  const isAddEditDepartmentTemplateLoading = useSelector(departmentTemplatesSelectors.isAddEditLoading);

  const [openedModal, setOpenedModal] = useState<StoreModal | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [departmentTemplate, setDepartmentTemplate] = useState<DepartmentTemplate | null>(null);
  const [filters, setFilters] = useState<FetchCrudData>({
    offset: 0,
    limit: ITEMS_PER_PAGE,
    sort: [StoreField.EXTERNAL_ID + ',' + SORT_ORDER.ASC],
  });

  useEffect(() => {
    if (filters.offset === 0) {
      dispatch(storeActions.getStoresAndDepartments(filters));
    } else if (filters.offset < total) {
      dispatch(storeActions.getMoreStoresAndDepartments(filters));
    }
  }, [filters]);

  const getDepartmentNames = departments => {
    const departmentNames = departments.map(department => department.departmentTemplate?.name || '-');
    return departmentNames.join(', ');
  };

  useEffect(() => {
    if (
      !isAddEditStoreLoading &&
      !isDeleteStoreLoading &&
      !isDeleteDepartmentTemplateLoading &&
      !isAddEditDepartmentTemplateLoading
    ) {
      dispatch(storeActions.getStoresAndDepartments(filters));
      dispatch(
        departmentTemplatesActions.getDepartmentTemplates({
          sort: [`${DepartmentTemplateField.CREATED_AT},ASC`],
        }),
      );
    }
  }, [isAddEditStoreLoading, isDeleteStoreLoading, isDeleteDepartmentTemplateLoading, isAddEditDepartmentTemplateLoading]);

  const editStore = (store: Store) => {
    setStore(store);
    setOpenedModal('ADD_EDIT_STORE');
  };

  const openDeleteStoreModal = (store: Store) => {
    setStore(store);
    setOpenedModal('DELETE_STORE');
  };

  const renderTableCell = (value: string, field: string, store: Store) => {
    switch (field) {
      case 'departments':
        return getDepartmentNames(store.departments) || '-';

      case 'defaultLanguage':
        return value ? store.defaultLanguage.language : '-';

      case 'actions':
        return (
          <>
            <Button theme={Theme.TRANSPARANT} icon='Edit' onClickCallback={() => editStore(store)} />
            <Button theme={Theme.TRANSPARANT} icon='Trashcan' onClickCallback={() => openDeleteStoreModal(store)} />
          </>
        );

      default:
        return value || '-';
    }
  };

  const getData = (options: GetTableDataOptions) => {
    setFilters((filters): FetchCrudData => {
      return {
        ...filters,
        offset: filters.limit * options.page,
        sort: [options.sortColumn + ',' + options.sortOrder],
      };
    });
  };

  const addStore = () => {
    setStore(null);
    setOpenedModal('ADD_EDIT_STORE');
  };

  const addDepartmentTemplateCallback = () => {
    setDepartmentTemplate(null);
    setOpenedModal('ADD_EDIT_DEPARTMENT_TEMPLATE');
  };

  const editDepartmentTemplateCallback = (editDepartmentTemplate: DepartmentTemplate) => {
    setDepartmentTemplate(editDepartmentTemplate);
    setOpenedModal('ADD_EDIT_DEPARTMENT_TEMPLATE');
  };

  const deleteDepartmentTemplateCallback = (deleteDepartmentTemplate: DepartmentTemplate) => {
    setDepartmentTemplate(deleteDepartmentTemplate);
    setOpenedModal('DELETE_DEPARTMENT_TEMPLATE');
  };

  const onDeleteDepartmentTemplateConfirmed = () => {
    dispatch(departmentTemplatesActions.deleteDepartmentTemplate(departmentTemplate.id));
  };

  const afterAddEditStore = () => {
    setOpenedModal(null);
    dispatch(storeActions.getStoresAndDepartments(filters));
  };

  const deleteStoreConfirmed = () => {
    if (!store?.id) {
      return;
    }
    dispatch(storeActions.deleteStore(store?.id));
  };

  return (
    <div className='content'>
      <div className='sticky-header'>
        <div className='top-buttons'>
          <Button theme={Theme.SECONDARY} onClickCallback={() => setOpenedModal('MANAGE_DEPARTMENT_TEMPLATES')} icon='Edit'>
            {t('lblEditDepartments')}
          </Button>
          <Button theme={Theme.SECONDARY} onClickCallback={addStore} icon='Add'>
            {t('lblAddStore')}
          </Button>
        </div>
        <h1 className='title'>{t('titleStores')}</h1>
      </div>
      <section>
        <Table
          columns={STORE_COLUMNS}
          renderTableCell={renderTableCell}
          data={storesAndDepartments}
          getData={getData}
          page={Math.floor(filters.offset / ITEMS_PER_PAGE)}
          isLoading={isLoading}
          defaultSortColumn={StoreField.EXTERNAL_ID}
          defaultSortOrder={SORT_ORDER.ASC}
        />
        <AddEditStoreModal isOpen={openedModal === 'ADD_EDIT_STORE'} closeModal={afterAddEditStore} store={store} />
        <ConfirmDeleteModal
          isOpen={openedModal === 'DELETE_STORE'}
          closeModal={() => setOpenedModal(null)}
          onDeleteConfirmed={deleteStoreConfirmed}
          title={'titleDeleteStore'}
          description={'lblDeleteStore'}
          checkboxDescription={t('lblDeleteStoreConfirm', {
            name: store?.name,
          })}
          isDeleteLoading={isDeleteStoreLoading}
        />
        <ManageDepartmentTemplatesModal
          isOpen={openedModal === 'MANAGE_DEPARTMENT_TEMPLATES'}
          closeModal={() => setOpenedModal(null)}
          addDepartmentTemplateCallback={addDepartmentTemplateCallback}
          editDepartmentTemplateCallback={editDepartmentTemplateCallback}
          deleteDepartmentTemplateCallback={deleteDepartmentTemplateCallback}
        />
        <AddEditDepartmentTemplateModal
          isOpen={openedModal === 'ADD_EDIT_DEPARTMENT_TEMPLATE'}
          closeModal={() => setOpenedModal('MANAGE_DEPARTMENT_TEMPLATES')}
          departmentTemplate={departmentTemplate}
        />
        <ConfirmDeleteModal
          isOpen={openedModal === 'DELETE_DEPARTMENT_TEMPLATE'}
          closeModal={() => setOpenedModal('MANAGE_DEPARTMENT_TEMPLATES')}
          onDeleteConfirmed={onDeleteDepartmentTemplateConfirmed}
          title='titleDeleteDepartment'
          description='lblDeleteDepartment'
          checkboxDescription={t('lblDeleteDepartmentConfirm', { name: departmentTemplate?.name })}
          isDeleteLoading={isDeleteDepartmentTemplateLoading}
        />
      </section>
    </div>
  );
};

export default AdminStoreAndDepartment;
